import {computed, runInInjectionContext, signal, Signal, WritableSignal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {FormControl, FormControlStatus} from '@angular/forms';
import {MainInjector} from '@shared-types';

import {SignalFormControlValidation} from './signal-form-control-validation.model';

export type SignalFormControlStats = FormControlStatus;

export class SignalFormControl<TValue = any> {
	public _fc: FormControl;

	public value: Signal<TValue> = signal('' as any);

	public status: Signal<SignalFormControlStats>;

	public isDisabled: Signal<boolean>;

	public isValid: Signal<boolean>;
	public invalidValidations: Signal<SignalFormControlValidation[]> = computed(() => {
		const status = this.status();
		const validations = this.validations();
		if (!status) return [];
		return validations.filter((validation: SignalFormControlValidation) => validation.isInvalid(this));
	});

	public validations: WritableSignal<SignalFormControlValidation[]> = signal([]);
	public hasValidations: Signal<boolean> = computed(() => this.validations().length > 0);

	constructor(initialValue: TValue) {
		this._fc = new FormControl(initialValue);
		runInInjectionContext(MainInjector, () => {
			this.value = toSignal<TValue>(this._fc.valueChanges);
			this.status = toSignal<SignalFormControlStats>(this._fc.statusChanges, {injector: MainInjector});
			this.isValid = computed(() => this.status() === 'VALID');
			this.isDisabled = computed(() => (this.status() ? this._fc.disabled : false));
		});
	}

	public disable(): void {
		this._fc.disable();
	}

	public enable(): void {
		this._fc.enable();
	}

	public reset(): void {
		this._fc.reset();
	}

	public getValue<TValue>(): TValue {
		return this._fc.value;
	}

	public setValue(value: TValue) {
		this._fc.setValue(value);
		this.update();
	}

	public setValidation(...validations: SignalFormControlValidation[]): void {
		const currentValidations = this.validations();
		validations.forEach(validation => {
			if (!currentValidations.some(v => v.name === validation.name)) {
				currentValidations.push(validation);
			}
			this._fc.setValidators(currentValidations.map(v => v.validator));
		});
	}

	public update(): void {
		this._fc.updateValueAndValidity();
		this._fc.markAsDirty();
		this._fc.markAsTouched();
	}
}
